import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/base/ArticleCard';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';
import type { Category } from '@/mocks/categories';
import type { Article } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState<Category | null>(null);
  const [articles, setArticles] = useState<Article[]>([]);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  useEffect(() => {
    if (slug) {
      const cat = categories.find((c) => c.slug === slug);
      if (cat) {
        setActiveCategory(cat);
        setArticles(getArticlesByCategory(slug));
        document.title = `${cat.name} - aqdlt 官方平台`;
      }
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  useEffect(() => {
    const existingScript = document.getElementById('category-jsonld');
    if (existingScript) existingScript.remove();

    if (activeCategory) {
      const script = document.createElement('script');
      script.id = 'category-jsonld';
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: `${activeCategory.name} - aqdlt 官方平台`,
        description: activeCategory.description,
        url: `https://aqdlt.com/category/${slug}`,
        isPartOf: {
          '@type': 'WebSite',
          name: 'aqdlt 官方平台',
          url: 'https://aqdlt.com',
        },
      });
      document.head.appendChild(script);
    }

    return () => {
      const s = document.getElementById('category-jsonld');
      if (s) s.remove();
    };
  }, [activeCategory, slug]);

  if (!activeCategory) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">分类未找到</h2>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100">
          <Breadcrumb
            items={[{ label: '分类', path: '/category/zonghe-fuwu' }, { label: activeCategory.name }]}
          />

          <div className="w-full px-4 md:px-6 pb-8">
            <div className="max-w-6xl mx-auto">
              <div className="mb-8">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                  {activeCategory.name}
                </h1>
                <p className="text-sm text-foreground-600">{activeCategory.description}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Mobile filter button */}
              <div className="lg:hidden">
                <button
                  onClick={() => setMobileFilterOpen(!mobileFilterOpen)}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer"
                >
                  <i className="ri-filter-3-line"></i>
                  选择分类
                  <i className={`transition-transform ${mobileFilterOpen ? 'rotate-180' : ''} ri-arrow-down-s-line`}></i>
                </button>
              </div>

              {/* Sidebar */}
              <aside className={`${mobileFilterOpen ? 'block' : 'hidden'} lg:block w-full lg:w-60 flex-shrink-0`}>
                <nav className="lg:sticky lg:top-24 space-y-1">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-all duration-200 cursor-pointer ${
                        activeCategory.slug === cat.slug
                          ? 'bg-primary-50 text-primary-600 font-medium'
                          : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                      }`}
                    >
                      <span className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm ${
                        activeCategory.slug === cat.slug
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-100 text-foreground-500'
                      }`}>
                        <i className={cat.icon}></i>
                      </span>
                      {cat.name}
                    </Link>
                  ))}
                </nav>
              </aside>

              {/* Article list */}
              <div className="flex-1 min-w-0">
                {articles.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {articles.map((article) => (
                      <ArticleCard key={article.slug} article={article} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-20">
                    <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                      <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                    </div>
                    <p className="text-sm text-foreground-500">该分类暂无文章</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}